/**
 * RedMart product page: gallery track + thumbs + dots, variant picker,
 * quantity stepper, copy-to-clipboard offers, sale countdown, pin-code estimate
 * and the sticky add-to-cart bar. Without JS: the first variant and photo
 * show, Add to cart / Buy now post the form, the gallery still swipes.
 */

class RmProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-rm-product-json]');
    const data = json ? JSON.parse(json.textContent) : {};
    this.variants = data.variants ?? [];
    this.moneyFormat = data.money_format ?? '{{amount}}';
    this.track = this.querySelector('[data-rm-track]');
    this.form = this.querySelector('form[data-rm-add]');

    this.#initGallery();
    this.#initOptions();
    this.#initQuantity();
    this.#initSticky();
    this.#initMagicBuyNow();

    this.addEventListener('click', (event) => {
      const code = /** @type {HTMLElement} */ (event.target).closest('[data-rm-copy]');
      if (code) this.#copy(code);
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /* ---------- Gallery ---------- */

  #initGallery() {
    if (!this.track) return;
    const slides = [...this.track.children];

    this.addEventListener('click', (event) => {
      const thumb = /** @type {HTMLElement} */ (event.target).closest('[data-rm-thumb]');
      if (thumb) this.#goTo(Number(thumb.getAttribute('data-rm-thumb')));
    });

    let frame = 0;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.#mark(Math.min(index, slides.length - 1));
      });
    });
  }

  /** @param {number} index */
  #goTo(index) {
    if (!this.track) return;
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
    this.#mark(index);
  }

  /** @param {number} index */
  #mark(index) {
    for (const thumb of this.querySelectorAll('[data-rm-thumb]')) {
      thumb.toggleAttribute('aria-current', Number(thumb.getAttribute('data-rm-thumb')) === index);
      if (Number(thumb.getAttribute('data-rm-thumb')) === index) {
        thumb.setAttribute('aria-current', 'true');
        // Keep the active thumb visible in the thumb column, without moving the page.
        const rail = /** @type {HTMLElement | null} */ (thumb.parentElement);
        const el = /** @type {HTMLElement} */ (thumb);
        if (rail && rail.scrollHeight > rail.clientHeight) {
          if (el.offsetTop < rail.scrollTop) rail.scrollTop = el.offsetTop;
          else if (el.offsetTop + el.offsetHeight > rail.scrollTop + rail.clientHeight) {
            rail.scrollTop = el.offsetTop + el.offsetHeight - rail.clientHeight;
          }
        }
      }
    }
    for (const dot of this.querySelectorAll('[data-rm-dot]')) {
      dot.classList.toggle('is-on', Number(dot.getAttribute('data-rm-dot')) === index);
    }
    const counter = this.querySelector('[data-rm-index]');
    if (counter) counter.textContent = String(index + 1);
  }

  /* ---------- Variants ---------- */

  #initOptions() {
    this.addEventListener('change', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      if (!input.matches('[data-rm-option]')) return;

      const chosen = this.querySelector(`[data-rm-chosen="${input.getAttribute('data-rm-option')}"]`);
      if (chosen) chosen.textContent = input.value;

      const selected = [...this.querySelectorAll('[data-rm-option]:checked')].map((el) => /** @type {HTMLInputElement} */ (el).value);
      const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
      if (variant) this.#applyVariant(variant);
    });
  }

  #applyVariant(variant) {
    this.variant = variant;
    const idInput = this.querySelector('[data-rm-variant-id]');
    if (idInput) idInput.value = String(variant.id);

    const set = (selector, text) => {
      for (const el of this.querySelectorAll(selector)) {
        el.hidden = !text;
        if (text) el.textContent = text;
      }
    };
    set('[data-rm-price]', variant.price);
    set('[data-rm-mrp]', variant.mrp);
    set('[data-rm-save]', variant.save);
    set('[data-rm-off]', variant.off);

    for (const el of this.querySelectorAll('[data-rm-in-stock]')) el.hidden = !variant.available;
    for (const el of this.querySelectorAll('[data-rm-sold-out]')) el.hidden = variant.available;

    const low = this.querySelector('[data-rm-low]');
    if (low instanceof HTMLElement) {
      const threshold = Number(low.dataset.threshold);
      const left = variant.inventory;
      low.hidden = !(left !== null && left > 0 && left <= threshold);
      if (!low.hidden) {
        low.querySelector('[data-rm-low-count]').textContent = String(left);
        low.querySelector('[data-rm-low-fill]').style.width = `${Math.min(100, (left / Number(low.dataset.full)) * 100)}%`;
      }
    }

    this.#updateTotal();

    if (variant.media) {
      const slides = [...(this.track?.children ?? [])];
      const index = slides.findIndex((s) => s.getAttribute('data-media-id') === String(variant.media));
      if (index >= 0) this.#goTo(index);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url);
  }

  /* ---------- Quantity ---------- */

  #initQuantity() {
    this.addEventListener('click', (event) => {
      const step = /** @type {HTMLElement} */ (event.target).closest('[data-rm-step]');
      const input = this.#qtyInput();
      if (!step || !input || !this.contains(step)) return;
      input.value = String(this.#clampQty(Number(input.value) + Number(step.getAttribute('data-rm-step'))));
      this.#updateTotal();
    });
    this.addEventListener('change', (event) => {
      if (/** @type {HTMLElement} */ (event.target).matches('[data-rm-qty-input]')) this.#updateTotal();
    });
    this.#updateTotal();
  }

  /** @returns {HTMLInputElement | null} */
  #qtyInput() {
    return this.querySelector('[data-rm-qty-input]');
  }

  /** @param {number} value */
  #clampQty(value) {
    const max = Number(this.#qtyInput()?.max) || 99;
    return Math.min(max, Math.max(1, Math.round(value) || 1));
  }

  /** @param {number} cents */
  #money(cents) {
    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) =>
      key === 'amount_no_decimals'
        ? Math.round(cents / 100).toLocaleString('en-IN')
        : (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    );
  }

  #updateTotal() {
    const variant = this.variant ?? this.variants.find((v) => String(v.id) === this.querySelector('[data-rm-variant-id]')?.value) ?? this.variants[0];
    if (!variant) return;

    const input = this.#qtyInput();
    const qty = input ? this.#clampQty(Number(input.value)) : 1;
    if (input) input.value = String(qty);
    const hidden = this.querySelector('[data-rm-qty]');
    if (hidden) hidden.value = String(qty);
    for (const step of this.querySelectorAll('[data-rm-step]')) {
      const dir = step.getAttribute('data-rm-step');
      step.disabled = dir === '-1' ? qty <= 1 : qty >= this.#clampQty(Infinity);
    }

    const total = qty === 1 ? variant.price : this.#money(variant.cents * qty);
    for (const el of this.querySelectorAll('[data-rm-total]')) el.textContent = total;
    const totalMrp = this.querySelector('[data-rm-total-mrp]');
    if (totalMrp && variant.mrp_cents) totalMrp.textContent = this.#money(variant.mrp_cents * qty);
  }

  /* ---------- Offers ---------- */

  /** @param {Element} button */
  async #copy(button) {
    const code = button.getAttribute('data-rm-copy') ?? '';
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }
    button.classList.add('is-copied');
    button.textContent = 'Copied';
    window.setTimeout(() => {
      button.classList.remove('is-copied');
      button.textContent = code;
    }, 2400);
  }

  /* ---------- Buy now -> Razorpay Magic Checkout ----------
     Razorpay's script only replaces Shopify's own "Buy it now" button, so our
     button hands itself to its buy-now entry point. If the script is missing
     (or Razorpay renames it), the form posts as before and goes to /checkout.
     Razorpay's buy now empties the cart and adds this item, so the cart is
     saved first and put back when Magic closes. */

  #initMagicBuyNow() {
    const button = this.querySelector('[data-rm-buy-now]');
    button?.addEventListener('click', async (event) => {
      if (typeof window.onBuyNowClickMagic !== 'function') return;
      event.preventDefault();
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      const saved = await cartSnapshot();
      button.removeAttribute('aria-busy');
      window.onBuyNowClickMagic(event);
      if (saved) restoreCartWhenMagicCloses(saved);
    });
  }

  /* ---------- Sticky add-to-cart ---------- */

  #initSticky() {
    const sticky = this.querySelector('[data-rm-sticky]');
    const buy = this.querySelector('[data-rm-buy]');
    if (!(sticky instanceof HTMLElement) || !buy) return;
    this.observer = new IntersectionObserver(([entry]) => {
      // Show once the buy buttons have scrolled above the viewport.
      sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    });
    this.observer.observe(buy);
  }
}

/* Sale countdown: data-ends="2026-10-31 23:59" (India time). Hidden when past. */
class RmSaleCountdown extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    const [date, time = '23:59'] = (this.dataset.ends ?? '').trim().split(/\s+/);
    this.end = Date.parse(`${date}T${time}:00+05:30`);
    if (Number.isNaN(this.end)) return;
    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #tick() {
    const left = Math.floor((this.end - Date.now()) / 1000);
    this.hidden = left <= 0;
    if (left <= 0) return window.clearInterval(this.#timer);
    const pad = (n) => String(n).padStart(2, '0');
    const values = { days: Math.floor(left / 86400), hrs: Math.floor((left % 86400) / 3600), min: Math.floor((left % 3600) / 60), sec: left % 60 };
    for (const [unit, value] of Object.entries(values)) {
      const el = this.querySelector(`[data-rm-unit="${unit}"]`);
      if (el) el.textContent = pad(value);
    }
  }
}

/* Pin-code check, phase 1: any valid 6-digit pin shows the store's delivery time. */
class RmPinCheck extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('[data-rm-pin]');
    const run = () => {
      const ok = /^[1-9][0-9]{5}$/.test(input.value.trim());
      this.querySelector('[data-rm-pin-result]').hidden = !ok;
      this.querySelector('[data-rm-pin-error]').hidden = ok;
    };
    this.querySelector('[data-rm-pin-check]')?.addEventListener('click', run);
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        run();
      }
    });
  }
}

/**
 * Formats cents with the store's money format ({{amount}}, {{amount_no_decimals}}, …).
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const group = (value, decimals, thousands, decimal) => {
    const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(cents, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(cents, 0, '.', ',');
      default:
        return group(cents, 2, ',', '.');
    }
  });
}

/* Recommendations: the page renders picked or same-collection products straight
   away. Only when it has none does this load Shopify's own recommendations,
   right after load (not on scroll), while the rail is still below the fold. */
class RmRecommendations extends HTMLElement {
  connectedCallback() {
    const ready = this.querySelector('[data-rm-recs-ready]');
    if (ready) this.#initBundle();
    if (ready && !ready.hasAttribute('data-rm-recs-fallback')) return;
    const start = () => this.#load();
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 1500 });
    else window.setTimeout(start, 200);
  }

  async #load() {
    for (const url of [this.dataset.url, this.dataset.fallbackUrl]) {
      if (!url) continue;
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.querySelector('rm-recommendations [data-rm-recs-ready]');
        if (!fresh) continue;
        this.replaceChildren(fresh);
        this.#initBundle();
        return;
      } catch (error) {
        console.error('[RedMart] recommendations failed', error);
      }
    }
    if (this.querySelector('[data-rm-recs-ready]')) return; // keep the fallback
    // Nothing to show: hide the in-page nav link that points here.
    for (const link of document.querySelectorAll('a[href="#bundle"]')) {
      if (!document.getElementById('bundle')) link.hidden = true;
    }
  }

  #initBundle() {
    const form = this.querySelector('[data-rm-bundle]');
    if (!(form instanceof HTMLFormElement)) return;
    const format = this.dataset.moneyFormat ?? '{{amount}}';
    const update = () => {
      const picked = [...form.querySelectorAll('[data-rm-bundle-item]:checked')];
      const sum = (key) => picked.reduce((total, el) => total + Number(el.getAttribute(key)), 0);
      form.querySelector('[data-rm-bundle-count]').textContent = `${picked.length} ${picked.length === 1 ? 'item' : 'items'}`;
      form.querySelector('[data-rm-bundle-price]').textContent = formatMoney(sum('data-price'), format);
      const mrp = form.querySelector('[data-rm-bundle-mrp]');
      mrp.textContent = sum('data-mrp') > sum('data-price') ? formatMoney(sum('data-mrp'), format) : '';
      const button = form.querySelector('[data-rm-bundle-button]');
      button.textContent = picked.length === 1 ? 'Add to cart' : `Add all ${picked.length}`;
      button.disabled = picked.length === 0;
    };
    form.addEventListener('change', update);
    update();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const drawer = document.querySelector('rm-cart-drawer');
      const items = [...form.querySelectorAll('[data-rm-bundle-item]:checked')].map((el) => ({ id: Number(el.value), quantity: 1 }));
      if (!items.length) return;
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: 'rm-cart-drawer' }),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (drawer && 'render' in drawer) {
        drawer.render(data.sections['rm-cart-drawer']);
        drawer.open();
      }
    });
  }
}

/* Cart save / restore around Razorpay's buy now.
   Magic's popup is .razorpay-container; it carries .razorpay-container-visible
   while open. After an order Razorpay empties the cart itself, so putting the
   saved cart back is right either way: it never held the buy-now item. */
const CART_ROOT = window.Shopify?.routes?.root ?? '/';

/** @returns {Promise<Array<{id: number, quantity: number, properties: object, selling_plan?: number}> | null>} */
async function cartSnapshot() {
  try {
    const response = await fetch(`${CART_ROOT}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const cart = await response.json();
    return cart.items.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      properties: item.properties ?? {},
      ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}),
    }));
  } catch {
    return null;
  }
}

/** @param {Array<object>} items */
function restoreCartWhenMagicCloses(items) {
  let opened = false;
  const observer = new MutationObserver(() => {
    const visible = document.querySelector('.razorpay-container')?.classList.contains('razorpay-container-visible');
    if (visible) opened = true;
    else if (opened) {
      observer.disconnect();
      restoreCart(items);
    }
  });
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  // Magic never opened (it fell back to Shopify checkout or failed): stop watching.
  setTimeout(() => {
    if (!opened) observer.disconnect();
  }, 60000);
}

/** @param {Array<object>} items */
async function restoreCart(items) {
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
  try {
    await fetch(`${CART_ROOT}cart/clear.js`, { method: 'POST', headers: json, body: '{}' });
    if (items.length) {
      await fetch(`${CART_ROOT}cart/add.js`, { method: 'POST', headers: json, body: JSON.stringify({ items }) });
    }
  } finally {
    const drawer = document.querySelector('rm-cart-drawer');
    if (drawer && 'refresh' in drawer) drawer.refresh();
  }
}

/* Live delivery dates, India time.
   [data-rm-eta][data-min][data-max] -> "<prefix> <strong>Thu, 30 Oct – Sun, 2 Nov</strong>"
   [data-rm-date][data-days]         -> "Sat, 26 Oct" */
function fillDeliveryDates() {
  const format = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
  const inDays = (days) => format.format(new Date(Date.now() + Number(days) * 86400000));
  for (const el of document.querySelectorAll('[data-rm-eta][data-min][data-max]')) {
    const strong = document.createElement('strong');
    strong.textContent = `${inDays(el.getAttribute('data-min'))} – ${inDays(el.getAttribute('data-max'))}`;
    el.replaceChildren(`${el.getAttribute('data-prefix') ?? 'Get it by'} `, strong);
  }
  for (const el of document.querySelectorAll('[data-rm-date][data-days]')) {
    el.textContent = inDays(el.getAttribute('data-days'));
  }
}
fillDeliveryDates();

/* Reviews band: our "Write a review" button opens Judge.me's form. If the app
   did not load, show the hidden widget instead. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-rm-write-review]');
  if (!trigger) return;
  const section = trigger.closest('.rm-reviews');
  const judgeme = section?.querySelector('[data-testid="write-review-button"], .jdgm-write-rev-link');
  if (judgeme) {
    judgeme.click();
    return;
  }
  const app = section?.querySelector('[data-rm-reviews-app]');
  if (app) {
    app.hidden = false;
    app.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});

if (!customElements.get('rm-recommendations')) customElements.define('rm-recommendations', RmRecommendations);
if (!customElements.get('rm-product')) customElements.define('rm-product', RmProduct);
if (!customElements.get('rm-sale-countdown')) customElements.define('rm-sale-countdown', RmSaleCountdown);
if (!customElements.get('rm-pin-check')) customElements.define('rm-pin-check', RmPinCheck);
